'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getSession } from 'next-auth/react';
import { api } from '@/shared/lib/api';
import type { SessionConfig } from '@/shared/lib/types';

interface SessionState {
  sessionId: string | null;
  token: string | null;
  config: SessionConfig | null;
  isLoading: boolean;
  error: string | null;

  createSession: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  updateConfig: (config: Partial<SessionConfig>) => Promise<void>;
  clearSession: () => void;
  expireSession: () => void;
  refreshToken: () => Promise<void>;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      sessionId: null,
      token: null,
      config: null,
      isLoading: false,
      error: null,

      createSession: async () => {
        set({ isLoading: true, error: null });
        try {
          const authSession = await getSession();
          const email = authSession?.user?.email;
          if (!email) throw new Error('Utilizador não autenticado');
          const { session_id, token } = await api.createSession(email);
          api.setSessionId(session_id);
          api.setSessionToken(token);

          const session = await api.getSession(session_id);

          set({
            sessionId: session_id,
            token,
            config: session.config,
            isLoading: false,
          });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      loadSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        try {
          api.setSessionId(sessionId);
          // Repõe o JWT persistido no cliente antes de qualquer pedido autenticado.
          const { token } = get();
          if (token) api.setSessionToken(token);
          const session = await api.getSession(sessionId);

          set({
            sessionId,
            config: session.config,
            isLoading: false,
          });
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
        }
      },

      updateConfig: async (config: Partial<SessionConfig>) => {
        const { sessionId } = get();
        if (!sessionId) return;

        try {
          const updated = await api.updateConfig(config);
          set({ config: updated });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      clearSession: () => {
        const { sessionId } = get();
        if (sessionId) {
          api.deleteSession(sessionId).catch((error) => {
            console.error('Erro ao remover sessão:', error);
          });
        }
        api.setSessionId(null);
        api.setSessionToken(null);
        set({ sessionId: null, token: null, config: null, error: null });
      },

      // Renova o JWT de sessão perto da expiração (ver useLoginLayout), sem passar por
      // isLoading/error de UI — é um refresh silencioso em segundo plano. Em falha
      // (ex. tempo de vida máximo excedido), propaga o erro para quem chamou decidir
      // forceLogout(); não mexe no estado aqui.
      refreshToken: async () => {
        const { sessionId } = get();
        if (!sessionId) return;
        const { token } = await api.refreshSession(sessionId);
        api.setSessionToken(token);
        set({ token });
      },

      // Sessão de backend expirada/rejeitada (401): limpa o estado local SEM chamar
      // api.deleteSession — o token já é inválido e chamá-lo só geraria outro 401.
      // Ao pôr sessionId a null, o useLoginLayout reavalia e mostra o ecrã de login.
      expireSession: () => {
        api.setSessionId(null);
        api.setSessionToken(null);
        set({ sessionId: null, token: null, config: null, error: null });
      },
    }),
    {
      name: 'rag-session',
      partialize: (state) => ({ sessionId: state.sessionId, token: state.token, config: state.config }),
    }
  )
);
